/**
 * command-bar.js — the two-row frame.
 *
 * ── Why it lands atomically ───────────────────────────────────────────────
 * `ui/toolbar.js` builds the entire bar in one `_render()` and `ui/shell.js`
 * constructs exactly one of them. A phase that replaced "row 1's left region"
 * would leave the middle controls with no owner, and mounting both classes at
 * once would duplicate DOM ids and event listeners.
 *
 * So the whole frame appears in one go, and every region that has not been
 * rewritten yet is filled by its EXISTING controller through a named
 * compatibility slot. Slots are then replaced one at a time.
 *
 * ── The one-owner invariant ───────────────────────────────────────────────
 * Every slot is `legacy` or `command-bar`, never both and never neither. The
 * manifest below is the single declaration of that, so the gate has something
 * to check against rather than inferring ownership from the DOM. CMD-9 cannot
 * close while any slot is still `legacy` — that is what stops the scaffolding
 * becoming permanent.
 *
 * ── What row 2 owns on day one ────────────────────────────────────────────
 * The two ANCHORS, and only those:
 *   left  — undo / redo, rebuilt on the kit
 *   right — the viewport controls, RELOCATED (not rewritten; CMD-5 owns the
 *           split into ViewportSwitcher + ZoomControl and the kit conversion)
 *
 * Both were removed from `ui/toolbar.js` in the same commit, so neither has two
 * owners. The contextual zone between them is empty until CMD-4 brings the Page
 * pane; it reserves its height from the start so that filling it later is not a
 * layout change.
 */

import { eventBus } from '../../core/events.js';
import { store } from '../../core/state.js';
import { createButton } from '../components.js';
import { icon } from '../icons.js';
import { ViewportSwitcher } from './viewport-switcher.js';
import { ScrollStateToggle } from './scroll-state-toggle.js';
import { ZoomControl } from './zoom-control.js';
import { SelectionScope } from './selection-scope.js';
import { PageTabs } from './page-tabs.js';
import { IdentityRegion } from './identity-region.js';
import { SavePill } from './save-pill.js';
import { DestinationRegion } from './destination-region.js';
import { ContextZone } from './context-zone.js';
import { MobileBar } from './mobile-bar.js';
import { HelpMenu } from './help-menu.js';
import { PreviewToggle } from './preview-toggle.js';
import { CompositeFocusModel } from './focus-model.js';

/**
 * Every region of the bar, and who currently renders it.
 *
 * `legacy`      — `ui/toolbar.js` renders it, mounted into this slot.
 * `command-bar` — this module renders it.
 *
 * Keep this list in sync with the phase that moves each one; the one-owner gate
 * reads it, and `docs/command-bar-census.md` cross-checks the ids.
 */
export const SLOTS = Object.freeze([
  {
    id: 'row1.identity',
    row: 1,
    group: 'row1-identity',
    owner: 'command-bar',
    note: 'Sidebar toggle, project chip and its menu. Moved in CMD-3.'
  },
  {
    id: 'row1.pages',
    row: 1,
    group: 'row1-identity',
    owner: 'command-bar',
    note: 'Page switcher. Moved in CMD-3.'
  },
  // DOM order puts what is LEFT of the old bar between the pages and the state
  // region, which is roughly where its three survivors (the ⌘K hint, the
  // preview toggle and the ⋮) already sat. It is not a design position — it is
  // the least disruptive place to park a shrinking slot.
  {
    id: 'row1.legacy',
    row: 1,
    group: 'row1-identity',
    owner: 'legacy',
    note: 'What is left of ui/toolbar.js.'
  },
  {
    id: 'row1.state',
    row: 1,
    group: 'row1-destination',
    owner: 'command-bar',
    note: 'Save pill and the presence stack. Moved in CMD-3.'
  },
  {
    id: 'row1.destination',
    row: 1,
    group: 'row1-destination',
    owner: 'command-bar',
    note: 'Agent, Share, Publish (split) and the account chip. Moved in CMD-3.'
  },
  {
    id: 'row2.history',
    row: 2,
    group: 'row2-history',
    owner: 'command-bar',
    note: 'Undo/redo. Never moves.'
  },
  {
    id: 'row2.mobile',
    row: 2,
    group: 'row2-mobile',
    owner: 'command-bar',
    note: 'Phone-only Pages + More triggers. Empty and zero-width on desktop.'
  },
  {
    id: 'row2.context',
    row: 2,
    group: 'row2-context',
    owner: 'command-bar',
    note: 'The morph zone. Panes landed in CMD-5.'
  },
  {
    id: 'row2.view',
    row: 2,
    group: 'row2-view',
    owner: 'command-bar',
    note: 'Viewport controls, preview toggle and Help. Never moves.'
  }
]);

/**
 * The composite focus model's groups, in TAB ORDER (contracts §3).
 *
 * A group is one Tab stop; ←/→ move inside it. Row 1's five slots collapse into
 * two groups because the row answers two questions — "where am I" and "how do I
 * ship it" — and a Tab stop per slot would be five stops for one row.
 *
 * `row2-mobile` is a SIXTH group the contract did not name. It holds the phone's
 * Pages and More triggers and is empty on a desktop, where an empty group offers
 * no stop and therefore costs no Tab press. Folding it into `row2-history` was
 * the alternative and would have put "Pages" inside a group labelled History.
 */
export const FOCUS_GROUPS = Object.freeze([
  { id: 'row1-identity', row: 1, label: 'Project and pages' },
  { id: 'row1-destination', row: 1, label: 'Save and publishing' },
  { id: 'row2-history', row: 2, label: 'History' },
  { id: 'row2-mobile', row: 2, label: 'Pages and more' },
  { id: 'row2-context', row: 2, label: 'Selection actions' },
  { id: 'row2-view', row: 2, label: 'View' }
]);

export class CommandBar {
  /**
   * @param {HTMLElement} container — `#pb-toolbar`
   * @param {HTMLIFrameElement} iframe — passed through to ViewportControls
   * @param {{initialAuthPayload?: object|null}} [opts] — the boot auth payload,
   *   INJECTED because `getCurrentUser()` lives in `shell.js` and no
   *   `command-bar/**` module may import it (gate 5).
   */
  constructor(container, iframe, opts = {}) {
    this._container = container;
    this._iframe = iframe;
    this._opts = opts;
    this._slots = new Map();
    this._offs = [];
    this._viewportSwitcher = null;
    this._zoomControl = null;
    this._scope = null;
    this._undoBtn = null;
    this._redoBtn = null;
    this._pageTabs = null;
    this._identity = null;
    this._savePill = null;
    this._destination = null;
    this._contextZone = null;
    this._mobileBar = null;
    this._previewToggle = null;
    this._helpMenu = null;
    this._focusModel = null;
    this._groupEls = new Map();
    this._render();
    this._mountAnchors();
    this._listen();
  }

  /** The element a legacy controller should render into. */
  getSlot(id) {
    return this._slots.get(id) || null;
  }

  /** Live ownership map — what the one-owner gate reads at runtime. */
  ownership() {
    return SLOTS.map((s) => ({ id: s.id, owner: s.owner, mounted: this._slots.has(s.id) }));
  }

  /**
   * Back-compat facade for the two halves. `shell.js` reaches for
   * `zoomIn/zoomOut/zoomReset` (the Ctrl+/Ctrl- shortcuts) and
   * `setLabelElement`; keeping one accessor means those call sites did not have
   * to learn about the split.
   */
  get viewportControls() {
    const switcher = this._viewportSwitcher;
    const zoom = this._zoomControl;
    if (!switcher || !zoom) return null;
    return {
      set: (name) => switcher.set(name),
      get current() {
        return switcher.current;
      },
      setLabelElement: (el) => switcher.setLabelElement(el),
      zoomIn: () => zoom.zoomIn(),
      zoomOut: () => zoom.zoomOut(),
      zoomReset: () => zoom.zoomReset()
    };
  }

  get viewportSwitcher() {
    return this._viewportSwitcher;
  }

  get zoomControl() {
    return this._zoomControl;
  }

  get pageTabs() {
    return this._pageTabs;
  }

  get identity() {
    return this._identity;
  }

  get savePill() {
    return this._savePill;
  }

  get destination() {
    return this._destination;
  }

  get contextZone() {
    return this._contextZone;
  }

  get mobileBar() {
    return this._mobileBar;
  }

  /**
   * Where `collab-ui.js` renders the peer avatar stack. It used to find its own
   * host by querying `.pb-toolbar-left` and inserting before `#pb-auth-chip`;
   * both of those were structural assumptions about a bar being rebuilt.
   */
  get presenceMount() {
    return this._savePill?.presenceMount || null;
  }

  /** The live scope, for panes mounted later. */
  get scope() {
    return this._scope;
  }

  _render() {
    this._container.classList.add('pb-cb');

    const row1 = document.createElement('div');
    row1.className = 'pb-cb-row pb-cb-row1';
    row1.setAttribute('role', 'toolbar');
    row1.setAttribute('aria-label', 'Project and publishing');

    const row2 = document.createElement('div');
    row2.className = 'pb-cb-row pb-cb-row2';
    row2.setAttribute('role', 'toolbar');
    row2.setAttribute('aria-label', 'Editing actions');
    // The row's scope state, read by the panes. `page` until a selection lands.
    row2.dataset.ctx = 'page';

    // One element per focus group, in tab order, so the composite focus model
    // has something to hang `role="group"` and its roving tabindex on. Row 2's
    // groups are its slots; row 1's two groups WRAP slots, which is why they
    // carry the row's own gap and the `margin-inline-start: auto` that used to
    // sit on the state slot.
    this._groupEls = new Map();
    for (const group of FOCUS_GROUPS.filter((g) => g.row === 1)) {
      const el = document.createElement('div');
      el.className = `pb-cb-group pb-cb-group-${group.id}`;
      row1.appendChild(el);
      this._groupEls.set(group.id, el);
    }

    for (const slot of SLOTS) {
      const el = document.createElement('div');
      el.className = `pb-cb-slot pb-cb-slot-${slot.id.split('.')[1]}`;
      el.dataset.slot = slot.id;
      el.dataset.owner = slot.owner;
      if (slot.row === 1) {
        this._groupEls.get(slot.group).appendChild(el);
      } else {
        // A row-2 group IS its single slot: the slot element is reused as the
        // group root rather than nested inside one, so nothing about row 2's
        // geometry — which the browser gate pins — moves in this phase.
        el.classList.add('pb-cb-group', `pb-cb-group-${slot.group}`);
        this._groupEls.set(slot.group, el);
        row2.appendChild(el);
      }
      this._slots.set(slot.id, el);
    }

    this._container.append(row1, row2);
    this._row2 = row2;
  }

  _mountAnchors() {
    // ── row 1 ──
    // Identity -> location -> state -> destination, left to right. That is the
    // reading order the design is built on and also the tab order the composite
    // focus model declares (contracts §3): the row answers "where am I / is it
    // safe / how do I ship it" and nothing else, and it never changes at any
    // selection.
    this._identity = new IdentityRegion(this._slots.get('row1.identity'), {
      initialAuthPayload: this._opts.initialAuthPayload ?? null
    });
    this._pageTabs = new PageTabs(this._slots.get('row1.pages'));
    this._savePill = new SavePill(this._slots.get('row1.state'));
    this._destination = new DestinationRegion(this._slots.get('row1.destination'), {
      initialAuthPayload: this._opts.initialAuthPayload ?? null
    });

    // ── left anchor: undo / redo ──
    // The ids move WITH the controls. An id names the control, not its
    // location: `tour-registry.js` walks the user to `#pb-undo-btn`, and the
    // census tracks it. Re-homing a button is not the same as deleting it, and
    // the one-owner test proves the legacy bar no longer has them.
    const history = this._slots.get('row2.history');
    this._undoBtn = createButton({
      variant: 'bare',
      className: 'pb-cb-icon',
      id: 'pb-undo-btn',
      icon: icon('undo'),
      ariaLabel: 'Undo',
      tooltip: 'Undo',
      tooltipShortcut: 'Ctrl+Z',
      onClick: () => store.undo()
    });
    this._redoBtn = createButton({
      variant: 'bare',
      className: 'pb-cb-icon',
      id: 'pb-redo-btn',
      icon: icon('redo'),
      ariaLabel: 'Redo',
      tooltip: 'Redo',
      tooltipShortcut: 'Ctrl+Shift+Z',
      onClick: () => store.redo()
    });
    const divider = document.createElement('span');
    divider.className = 'pb-cb-divider';
    divider.setAttribute('aria-hidden', 'true');
    history.append(this._undoBtn, this._redoBtn, divider);
    this._syncHistoryChrome(false, false);

    // ── right anchor: viewport controls ──
    // Relocated, not rewritten. CMD-5 splits it into ViewportSwitcher +
    // ZoomControl, migrates both to the kit, and shrinks its four
    // ui-consistency-sweep allowlist entries in that same commit.
    const view = this._slots.get('row2.view');
    const vpMount = document.createElement('div');
    vpMount.id = 'pb-viewport-controls';
    vpMount.className = 'pb-viewport-group';
    view.appendChild(vpMount);

    // CMD-5 — the split. Two products that shared one class now share one
    // NUMBER: the switcher derives `fitScale` from the preset and the space
    // available, the zoom control multiplies it by what the user chose, and
    // neither knows how the other works.
    //
    // Construction order matters: the zoom control reads the fit through a
    // getter, so the switcher must exist first — but it must not PUSH before
    // the zoom control exists, which is why `onFitScaleChange` is optional-
    // chained through a field assigned immediately after.
    this._viewportSwitcher = new ViewportSwitcher(vpMount, this._iframe, {
      onFitScaleChange: () => this._zoomControl?.push()
    });
    this._zoomControl = new ZoomControl(vpMount, {
      getFitScale: () => this._viewportSwitcher?.fitScale ?? 1
    });

    // LG-4 — "Scrolled". Deliberately a sibling of the switcher rather than a
    // fourth segment inside it: `VIEWPORTS` feeds the responsive-override bucket
    // name, and a fourth entry would silently discard overrides written while it
    // was active. It is also orthogonal to size — a header can be mobile AND
    // scrolled — which a radio group cannot express.
    this._scrollStateToggle = new ScrollStateToggle(vpMount);

    // CMD-8 — the last two controls the census had homed here and nobody had
    // moved. Preview was CMD-5's debt; Help is the ⋮'s final concept getting a
    // real surface, which is what makes the junk drawer deletable in CMD-9
    // rather than re-labelled.
    const viewDivider = document.createElement('span');
    viewDivider.className = 'pb-cb-divider';
    viewDivider.setAttribute('aria-hidden', 'true');
    view.appendChild(viewDivider);
    this._previewToggle = new PreviewToggle(view);
    this._helpMenu = new HelpMenu(view);
  }

  _listen() {
    this._offs.push(
      eventBus.on('history-changed', ({ canUndo, canRedo }) =>
        this._syncHistoryChrome(canUndo, canRedo)
      )
    );

    // The scope engine runs from here so panes mounted in later phases have a
    // live source the moment they appear; `data-ctx` on row 2 is its public
    // read-out.
    this._scope = new SelectionScope();
    this._scope.subscribe(({ identity }) => {
      if (this._row2) this._row2.dataset.ctx = identity.kind;
    });
    this._scope.start();

    // CMD-5 — the morph. Mounted AFTER `start()` so its first `subscribe`
    // receives the settled scope immediately (contract S2): a bar that boots
    // blank is a bug, and a pane that only appears after the first selection
    // change is the same bug with a longer fuse.
    this._contextZone = new ContextZone(this._slots.get('row2.context'), this._scope);

    // CMD-7 — the phone. Row 2 BECOMES the bottom bar (a CSS `position: fixed`
    // under the 640px query), so there is no second bar to keep in parity with
    // this one. What this adds is the two things a phone needs and a desktop
    // does not — the Pages and More sheets — plus the published height every
    // bottom-anchored surface offsets itself by.
    this._mobileBar = new MobileBar(this._row2, this._slots.get('row2.mobile'), {
      initialAuthPayload: this._opts.initialAuthPayload ?? null
    });

    // CMD-8 — the composite focus model, LAST: it reads the mounted controls to
    // decide which one in each group is tabbable, so every region has to exist
    // first. It re-reads on its own from there (a MutationObserver per group),
    // which is what carries it through the row-2 morph and the page strip's
    // collapse without anyone remembering to tell it.
    this._focusModel = new CompositeFocusModel(
      FOCUS_GROUPS.map((g) => ({ ...g, el: this._groupEls.get(g.id) }))
    );
  }

  /**
   * Disabled buttons do not reliably receive hover or focus, so the REASON goes
   * in the accessible name rather than in a tooltip. Same approach the legacy
   * toolbar's `_syncUndoRedoChrome` took, and for the same reason.
   */
  _syncHistoryChrome(canUndo, canRedo) {
    if (this._undoBtn) {
      this._undoBtn.disabled = !canUndo;
      this._undoBtn.setAttribute('aria-label', canUndo ? 'Undo' : 'Undo (nothing to undo)');
      if (canUndo) {
        this._undoBtn.dataset.tooltip = 'Undo';
        this._undoBtn.dataset.tooltipShortcut = 'Ctrl+Z';
      } else {
        delete this._undoBtn.dataset.tooltip;
        delete this._undoBtn.dataset.tooltipShortcut;
      }
    }
    if (this._redoBtn) {
      this._redoBtn.disabled = !canRedo;
      this._redoBtn.setAttribute('aria-label', canRedo ? 'Redo' : 'Redo (nothing to redo)');
      if (canRedo) {
        this._redoBtn.dataset.tooltip = 'Redo';
        this._redoBtn.dataset.tooltipShortcut = 'Ctrl+Shift+Z';
      } else {
        delete this._redoBtn.dataset.tooltip;
        delete this._redoBtn.dataset.tooltipShortcut;
      }
    }
  }

  get focusModel() {
    return this._focusModel;
  }

  get helpMenu() {
    return this._helpMenu;
  }

  get previewToggle() {
    return this._previewToggle;
  }

  destroy() {
    for (const off of this._offs) if (typeof off === 'function') off();
    this._offs = [];
    this._focusModel?.destroy();
    this._focusModel = null;
    this._helpMenu?.destroy();
    this._helpMenu = null;
    this._previewToggle?.destroy();
    this._previewToggle = null;
    this._scope?.destroy();
    this._scope = null;
    this._identity?.destroy();
    this._identity = null;
    this._pageTabs?.destroy();
    this._pageTabs = null;
    this._savePill?.destroy();
    this._savePill = null;
    this._destination?.destroy();
    this._destination = null;
    this._contextZone?.destroy();
    this._contextZone = null;
    this._mobileBar?.destroy();
    this._mobileBar = null;
    this._viewportSwitcher?.destroy?.();
    this._scrollStateToggle?.destroy?.();
    this._scrollStateToggle = null;
    this._viewportSwitcher = null;
    this._zoomControl?.destroy?.();
    this._zoomControl = null;
    this._slots.clear();
    this._undoBtn = null;
    this._redoBtn = null;
    this._row2 = null;
  }
}
